import i18next from "i18next";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

import { seo } from "../../seo";

const BASE_URL = "https://as.dushanbe24.ru";

const LANG_TO_HREFLANG = {
  ru: "ru",
  en: "en",
  tj: "tg",
};

const PATH_LABELS = {
  airlines: "Авиакомпании",
  flights: "Направления",
  about: "О компании",
  contacts: "Контакты",
  services: "Услуги",
  partners: "Партнёры",
  "payment-info": "Способы оплаты",
  "privacy-policy-info": "Политика конфиденциальности",
  "public-offer-info": "Публичная оферта",
  "rules-of-use-info": "Правила использования",
  scoreboard: "Табло",
};

const buildBreadcrumbList = (pathname, pageTitle) => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const items = [
    { "@type": "ListItem", position: 1, name: "Главная", item: BASE_URL },
  ];

  let currentPath = "";
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;
    const name = isLast && pageTitle ? pageTitle : (PATH_LABELS[segment] || segment);
    items.push({
      "@type": "ListItem",
      position: index + 2,
      name,
      item: `${BASE_URL}${currentPath}`,
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
};

const SeoTags = ({ pageKey, jsonLd }) => {
  const { language } = i18next;
  const location = useLocation();

  const canonicalUrl = `${BASE_URL}${location.pathname}`;

  const pageTitle = seo.titles[language]?.[pageKey] || seo.titles["ru"]?.[pageKey] || "";
  const pageMetaDescription = seo.metaDescriptions[language]?.[pageKey] || seo.metaDescriptions["ru"]?.[pageKey] || "";
  const pageMetaKeywords = seo.metaKeywords[language]?.[pageKey] || seo.metaKeywords["ru"]?.[pageKey] || "";
  const pageMetaTitle = seo.metaTitles[language]?.[pageKey] || seo.metaTitles["ru"]?.[pageKey] || "";
  const pageMetaOgObject = seo.metaOgObject[language]?.[pageKey] || seo.metaOgObject["ru"]?.[pageKey] || {};

  const ogUrl = pageMetaOgObject?.url || canonicalUrl;
  const ogImage = pageMetaOgObject?.image || `${BASE_URL}/images/og-default.png`;

  return (
    <Helmet>
      {!!pageTitle && <title>{pageTitle}</title>}

      {/* Базовые мета */}
      {!!pageMetaDescription && <meta name="description" content={pageMetaDescription} />}
      {!!pageMetaKeywords && <meta name="keywords" content={pageMetaKeywords} />}
      {!!pageMetaTitle && <meta name="title" content={pageMetaTitle} />}

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang для мультиязычности */}
      <link rel="alternate" hreflang="ru" href={`${BASE_URL}${location.pathname}`} />
      <link rel="alternate" hreflang="en" href={`${BASE_URL}${location.pathname}`} />
      <link rel="alternate" hreflang="tg" href={`${BASE_URL}${location.pathname}`} />
      <link rel="alternate" hreflang="x-default" href={`${BASE_URL}${location.pathname}`} />

      {/* Open Graph */}
      {!!pageMetaOgObject?.description && <meta property="og:description" content={pageMetaOgObject.description} />}
      <meta property="og:image" content={ogImage} />
      {!!pageMetaOgObject?.site_name && <meta property="og:site_name" content={pageMetaOgObject.site_name} />}
      {!!pageMetaOgObject?.title && <meta property="og:title" content={pageMetaOgObject.title} />}
      <meta property="og:type" content={pageMetaOgObject?.type || "website"} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:locale" content={LANG_TO_HREFLANG[language] === "ru" ? "ru_RU" : language === "en" ? "en_US" : "tg_TJ"} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      {!!pageTitle && <meta name="twitter:title" content={pageMetaOgObject?.title || pageTitle} />}
      {!!pageMetaDescription && <meta name="twitter:description" content={pageMetaOgObject?.description || pageMetaDescription} />}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD структурированные данные */}
      {!!jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}

      {/* BreadcrumbList */}
      {(() => {
        const breadcrumbs = buildBreadcrumbList(location.pathname, pageTitle);
        return breadcrumbs ? (
          <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
        ) : null;
      })()}
    </Helmet>
  );
};

export default SeoTags;
